import { ApiError } from '../client';
import { MeResponseSchema, type MeResponse } from '../schemas/me';

const CC = { id: 'cc', name: 'Customer Care' };
const PRODUCT = { id: 'product', name: 'Producto' };
const SYSTEMS = { id: 'systems', name: 'Sistemas' };

// One payload per combination of roles and permissions a screen has to handle. `access.*` only with
// the «Sistemas» role, the exclusive access-management role.
export const meFixtures = {
  // Both roles, so mock mode also shows permissions adding up.
  default: {
    roles: [CC, PRODUCT],
    permissions: [
      'care.search',
      'operations.amazon_door_access',
      'operations.carrier_exclusion_lists',
    ],
  },
  cc: { roles: [CC], permissions: ['care.search'] },
  // A per-person extra on top of a role.
  ccWithDoorAccess: { roles: [CC], permissions: ['care.search', 'operations.amazon_door_access'] },
  // An extra outside the role's space: the screen opens by URL, without Producto's side menu.
  ccWithExclusionLists: {
    roles: [CC],
    permissions: ['care.search', 'operations.carrier_exclusion_lists'],
  },
  productOnly: {
    roles: [PRODUCT],
    permissions: ['operations.amazon_door_access', 'operations.carrier_exclusion_lists'],
  },
  noPermissions: { roles: [], permissions: [] },
  // The person who assigns roles, and nothing more: no operational view comes with it.
  systemsOnly: { roles: [SYSTEMS], permissions: ['access.manage'] },
  // Breaks «Sistemas» exclusivity on purpose: the only way to see the Sistemas space offer another.
  ccAndSystems: { roles: [CC, SYSTEMS], permissions: ['care.search', 'access.manage'] },
} satisfies Record<string, MeResponse>;

export type MeFixture = keyof typeof meFixtures;

// Own keys only: a name read from config must never resolve to a prototype member.
export function isMeFixture(value: string | undefined): value is MeFixture {
  return value !== undefined && Object.hasOwn(meFixtures, value);
}

let current: MeFixture = 'default';
let forcedError: string | null = null;

export function setMeMockFixture(fixture: MeFixture): void {
  current = fixture;
}

// A server error, not a 401 or 403: a failed re-read of /me after a refusal has to reach the
// retryable error screen, which a sign-in problem would not.
export function setMeMockError(code: string | null): void {
  forcedError = code;
}

export function resetMeMock(): void {
  current = 'default';
  forcedError = null;
}

export async function getMeMock(fixture: MeFixture = current): Promise<MeResponse> {
  await new Promise((resolve) => setTimeout(resolve, 100));
  if (forcedError !== null) {
    throw new ApiError(500, forcedError, { code: forcedError }, `GET me failed: ${forcedError}`);
  }
  // Through the same parse as the real read; a parse also returns a fresh copy, so a caller that
  // mutates the result cannot change the fixture for the next read.
  return MeResponseSchema.parse(meFixtures[fixture]);
}
